import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography, Marker, Line } from "react-simple-maps";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const ORIGIN = [79.08, 21.14]; // Nagpur

const PINS = [
  { name: "USA", coord: [-100, 40], color: "#c92e34" },
  { name: "Canada", coord: [-95, 55], color: "#282829" },
  { name: "UK", coord: [-1.5, 53], color: "#c92e34" },
  { name: "Ireland", coord: [-8, 53], color: "#282829" },
  { name: "Europe", coord: [10, 50], color: "#c92e34" },
  { name: "Dubai", coord: [55, 25], color: "#282829" },
  { name: "Australia", coord: [134, -25], color: "#c92e34" },
  { name: "New Zealand", coord: [172, -41], color: "#282829" },
];

function PulsingMarker({ coord, color, name, delay = 0, mobile = false }) {
  return (
    <Marker coordinates={coord}>
      <motion.circle
        r={mobile ? 22 : 16}
        fill={color}
        opacity={0.3}
        animate={{ scale: [0.8, 1.7, 0.8], opacity: [0.45, 0, 0.45] }}
        transition={{ repeat: Infinity, duration: 2, ease: "easeOut", delay }}
      />
      <motion.circle
        r={mobile ? 11 : 8}
        fill={color}
        stroke="#fff"
        strokeWidth={mobile ? 3 : 2.5}
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 + delay, type: "spring", stiffness: 220 }}
      />
      <circle r={mobile ? 3.6 : 2.8} fill="#fff" />
      <text
        textAnchor="middle"
        y={mobile ? -20 : -15}
        fontSize={mobile ? 18 : 12}
        fontWeight={900}
        fill="#282829"
        style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: mobile ? 5 : 3.5, strokeLinejoin: "round" }}
      >
        {name.toUpperCase()}
      </text>
    </Marker>
  );
}

export default function WorldMap() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Big, stretched projection — scale proportional to width
  const projectionConfig = isMobile
    ? { scale: 220, center: [10, 18] }
    : { scale: 210, center: [10, 18] };

  return (
    <section
      className="grid-bg py-20 md:py-28 bg-white relative overflow-hidden"
      data-testid="world-map-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="text-center mb-10">
          <span className="inline-block bg-[#c92e34] text-white text-[10px] md:text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border-2 border-[#282829] rotate-2">
            8 Destinations · 1 Mission
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-7xl mt-4 leading-[0.9]">
            Your map to a <span className="gradient-text-rb">global</span> career.
          </h2>
          <p className="mt-5 text-sm md:text-base text-[#282829]/65 max-w-2xl mx-auto">
            Wherever your dream campus is — we've sent students there.
          </p>
        </div>

        {/* Full-bleed map frame */}
        <div className="relative bg-[#FAFAFA] border-2 border-[#282829] rounded-3xl p-2 sm:p-4 brutal-shadow-ink">
          <div className="w-full">
            <ComposableMap
              projection="geoEqualEarth"
              projectionConfig={projectionConfig}
              width={1200}
              height={isMobile ? 720 : 650}
              style={{ width: "100%", height: "auto", display: "block" }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      style={{
                        default: { fill: "#282829", stroke: "#FFFFFF", strokeWidth: 0.6, outline: "none" },
                        hover: { fill: "#c92e34", stroke: "#FFFFFF", strokeWidth: 0.6, outline: "none" },
                        pressed: { fill: "#c92e34", outline: "none" },
                      }}
                    />
                  ))
                }
              </Geographies>

              {PINS.map((p) => (
                <Line
                  key={`line-${p.name}`}
                  from={ORIGIN}
                  to={p.coord}
                  stroke={p.color}
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeDasharray="4 4"
                  opacity={0.9}
                />
              ))}

              <Marker coordinates={ORIGIN}>
                <motion.circle
                  r={isMobile ? 18 : 14}
                  fill="#282829"
                  opacity={0.2}
                  animate={{ scale: [1, 1.8, 1], opacity: [0.35, 0, 0.35] }}
                  transition={{ repeat: Infinity, duration: 2.2, ease: "easeOut" }}
                />
                <circle r={isMobile ? 9 : 7} fill="#282829" stroke="#fff" strokeWidth={isMobile ? 2.5 : 2} />
                <text
                  textAnchor="middle"
                  y={isMobile ? -20 : -15}
                  fontSize={isMobile ? 18 : 12}
                  fontWeight={900}
                  fill="#282829"
                  style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: isMobile ? 5 : 3.5, strokeLinejoin: "round" }}
                >
                  NAGPUR
                </text>
              </Marker>

              {PINS.map((p, i) => (
                <PulsingMarker key={p.name} coord={p.coord} color={p.color} name={p.name} delay={i * 0.12} mobile={isMobile} />
              ))}
            </ComposableMap>
          </div>
        </div>
      </div>
    </section>
  );
}
